/**
 * A track is a thing worth measuring.
 *
 * Nothing here knows what is being tracked — not food, not mood, not rainfall.
 * That is the point of the product and the easiest thing to erode: a field or a
 * branch that only makes sense for one subject area is a wrong turn, and the
 * general capability should be modelled instead.
 */

import { err, invalid, ok, type Result, type ValidationError } from './result.js'
import { isSafeColour } from './colour.js'
import { isPaletteColour, type PaletteColour } from './palette.js'
import type { Timestamp } from './timestamp.js'

export interface EnumOption {
  readonly id: string
  readonly label: string
  /**
   * Position on the scale for an ordered enum; `null` for an unordered one.
   *
   * This is what makes `none / mild / strong` chart as a line while
   * `breakfast / lunch / dinner` can only ever be counted.
   */
  readonly score: number | null
  readonly colour?: string
  /**
   * Retired from the picker, kept for history.
   *
   * An option that has been recorded cannot be deleted without orphaning every
   * measurement that chose it — the same problem archiving solves for tracks
   * (ADR-0003). Archiving removes it from the picker while `describeValue` goes
   * on resolving it, so an entry from last year still reads "Overwhelming"
   * rather than showing a bare id.
   */
  readonly archived?: boolean
}

/** Options a user can still choose. History resolves against all of them. */
export function selectableOptions(kind: EnumKind | MultiKind): readonly EnumOption[] {
  return kind.options.filter((option) => option.archived !== true)
}

/**
 * A word naming one point on a numeric scale (T-807).
 *
 * Sparse **anchors**, not one label per value. A 1–10 track is ten strings and
 * a 0–100 track is a hundred; asking for either is a first-run experience
 * nobody survives. Three anchors describe the same scale, and they degrade
 * gracefully when the range is edited later — a full array silently goes wrong
 * the moment someone moves the maximum.
 */
export interface ValueWord {
  /** The point on the scale this word names. */
  readonly at: number
  readonly label: string
}

export interface NumericKind {
  readonly kind: 'numeric'
  readonly min: number
  readonly max: number
  readonly step: number
  readonly decimals: number
  /**
   * Optional words for points on the scale.
   *
   * The scale is the user's own invention, so only they can say what 8 means.
   * Once they have, the app can stop pretending the number speaks for itself —
   * on screen, and to a screen reader through `aria-valuetext`, where "8" alone
   * is close to useless.
   */
  readonly words?: readonly ValueWord[]
}

export interface BooleanKind {
  readonly kind: 'boolean'
  readonly trueLabel: string
  readonly falseLabel: string
}

export interface EnumKind {
  readonly kind: 'enum'
  readonly ordered: boolean
  readonly options: readonly EnumOption[]
}

/**
 * A yes with no no: the only thing worth recording is that it happened.
 *
 * "Ate", "Studied", "Drank". A boolean nearly covers this and gets one thing
 * importantly wrong — it asks a *question* with two answers, so the user has to
 * distinguish "no" from "not answered" every single time. A marker has no
 * question. Recording it is the whole act, and its absence is simply the
 * absence of a record, which is what ADR-0005 already says about a skipped
 * track.
 *
 * **No fields, deliberately.** The track's name is the label, and a second
 * place to write the same word is the thing that later disagrees with it. A
 * name that reads badly on a button is what `question` is for.
 */
export interface MarkerKind {
  readonly kind: 'marker'
}

/**
 * Several of a set at once (T-1301).
 *
 * The distinction from `EnumKind` is the **selection**, not the options: an enum
 * asks "which one", this asks "which of these". Symptoms, ingredients, what you
 * did today — the cases where forcing a single answer means recording something
 * that is not what happened.
 *
 * It reuses `EnumOption` deliberately rather than defining a parallel type. The
 * id-not-label rule, archiving, colours and the whole option editor already work
 * on it, and a second option type would be a second place to reimplement all of
 * that. `score` must be `null`, for the same reason an unordered enum requires
 * it: there is no single value to score.
 */
export interface MultiKind {
  readonly kind: 'multi'
  readonly options: readonly EnumOption[]
}

/**
 * A discriminated union, not a `type` field beside a bag of optional
 * properties. The compiler then makes reading `min` off a boolean track
 * impossible rather than merely wrong, and adding another kind turns every
 * unhandled `switch` into a compile error instead of a silent fall-through.
 *
 * That is not a claim about the past: `marker` (T-1001) and `multi` (T-1301)
 * were both added by fixing what the compiler objected to, fifteen sites for
 * the second one.
 */
export type TrackKind = NumericKind | BooleanKind | EnumKind | MarkerKind | MultiKind

export type TrackKindName = TrackKind['kind']

export interface Track {
  readonly id: string
  readonly name: string
  readonly unit?: string
  readonly description?: string
  /**
   * How this track introduces itself when something asks for a value.
   *
   * Optional, and absent is the normal case — `questionFor` derives
   * `` `${name}?` `` when it is not set, so most tracks never need one. It
   * exists for the cases where the name and the question want to be different
   * things: a track called "Sleep quality" asking "How rested do you feel?".
   *
   * Deliberately **not** copied from the name at creation time. A stored copy
   * goes stale the moment the track is renamed, and a track that spends a year
   * asking about a name it no longer has is a bug nobody looks for.
   */
  readonly question?: string
  /**
   * The colour chosen for this track, by name (see `palette.ts`).
   *
   * Optional: a track nobody has picked a colour for is drawn in the colour
   * its id gives, through `colourOfTrack`, so every track created before this
   * field existed keeps the colour it already had.
   */
  readonly colour?: PaletteColour
  /**
   * A heading to list this track under, such as the name of the set it came
   * from. Free text and purely for arranging lists: nothing is calculated
   * from it, and an absent group is normal.
   */
  readonly group?: string
  readonly kind: TrackKind
  /** Archived tracks keep their history and disappear from logging (ADR-0003). */
  readonly archived: boolean
  readonly position: number
  readonly createdAt: Timestamp
  readonly updatedAt: Timestamp
}

/**
 * A recorded value.
 *
 * Distinguishable without a tag — `typeof` separates the first three and
 * `Array.isArray` the fourth — and the track it belongs to is always the
 * authority on which one is expected. A string is an enum **option id**, never
 * a label: renaming an option must not rewrite history, and the same holds for
 * every id in the array.
 *
 * The array arrived with `multi` (T-1301) and is the one member that is not a
 * primitive. Anything narrowing a `TrackValue` by `typeof` alone reads it as an
 * object, so check `Array.isArray` first.
 */
export type TrackValue = number | boolean | string | readonly string[]

/**
 * The option ids a recorded value points at: one for an enum, several for a
 * multi-select, none for anything else.
 *
 * Here rather than at each call site because every caller is really asking the
 * same question — *may this option be deleted, or must it be archived?* — and
 * an answer that forgot the array member would let a chosen option be removed
 * out from under the entries holding it. Narrowed by `typeof` alone: once the
 * three primitives are excluded the list is all that remains, and
 * `Array.isArray` widens a `readonly string[]` to `any[]` on the way past.
 */
export function chosenOptionIds(value: TrackValue): readonly string[] {
  if (typeof value === 'string') {
    return [value]
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return []
  }
  return value
}

// ─── Exhaustive handling ──────────────────────────────────────────────────────

export interface TrackKindHandlers<T> {
  readonly numeric: (kind: NumericKind) => T
  readonly boolean: (kind: BooleanKind) => T
  readonly enum: (kind: EnumKind) => T
  readonly marker: (kind: MarkerKind) => T
  readonly multi: (kind: MultiKind) => T
}

/**
 * Handles every track kind, or fails to compile.
 *
 * Prefer this to a bare `switch` at call sites that must stay total when a
 * fourth kind arrives — the missing handler becomes a type error at every
 * consumer at once.
 */
export function matchTrackKind<T>(kind: TrackKind, handlers: TrackKindHandlers<T>): T {
  switch (kind.kind) {
    case 'numeric':
      return handlers.numeric(kind)
    case 'boolean':
      return handlers.boolean(kind)
    case 'enum':
      return handlers.enum(kind)
    case 'marker':
      return handlers.marker(kind)
    case 'multi':
      return handlers.multi(kind)
  }
}

export const isNumericKind = (kind: TrackKind): kind is NumericKind =>
  kind.kind === 'numeric'
export const isBooleanKind = (kind: TrackKind): kind is BooleanKind =>
  kind.kind === 'boolean'
export const isEnumKind = (kind: TrackKind): kind is EnumKind => kind.kind === 'enum'
export const isMultiKind = (kind: TrackKind): kind is MultiKind => kind.kind === 'multi'
export const isMarkerKind = (kind: TrackKind): kind is MarkerKind =>
  kind.kind === 'marker'

/**
 * Whether this track can produce a mean at all.
 *
 * Ordered enums can, via their scores. Unordered ones cannot: an average of
 * `breakfast / lunch / dinner` is a number that looks meaningful and is not,
 * and this app generates documents people make decisions from.
 */
export function isQuantitative(kind: TrackKind): boolean {
  return matchTrackKind(kind, {
    numeric: () => true,
    boolean: () => true,
    enum: (k) => k.ordered,
    /*
     * A marker has no magnitude at all, so it is not quantitative and this is
     * what keeps it out of every mean, trend and overlay in one place.
     */
    marker: () => false,
    /*
     * Several answers have no single magnitude between them, so there is
     * nothing to average — the same position as an unordered enum reached from
     * the other direction. What a multi-select has is a count per option.
     */
    multi: () => false,
  })
}

// ─── Validation ───────────────────────────────────────────────────────────────

const MAX_NAME_LENGTH = 80
const MAX_DESCRIPTION_LENGTH = 500
/* Shorter than a description on purpose: it is a headline on a phone, and one
   that wraps to four lines has stopped being a question and become a form. */
const MAX_QUESTION_LENGTH = 120
const MAX_GROUP_LENGTH = 40
const MAX_DECIMALS = 6
const MIN_ENUM_OPTIONS = 2
const MAX_WORD_LENGTH = 40
/* Sparse is the whole design. Past a dozen the author is labelling every value,
   which is the thing anchors exist to avoid — and a control cannot show that
   many words anyway. */
const MAX_WORDS = 12

function validateNumericKind(kind: NumericKind): ValidationError[] {
  const errors: ValidationError[] = []
  const { min, max, step, decimals } = kind

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    errors.push(
      invalid(
        'track.numeric.bounds.invalid',
        'Minimum and maximum must be numbers.',
        'kind',
      ),
    )
    return errors
  }
  if (min >= max) {
    errors.push(
      invalid(
        'track.numeric.bounds.inverted',
        `Minimum (${String(min)}) must be less than maximum (${String(max)}).`,
        'kind.min',
      ),
    )
  }
  if (!Number.isFinite(step) || step <= 0) {
    errors.push(
      invalid(
        'track.numeric.step.invalid',
        'Step must be greater than zero.',
        'kind.step',
      ),
    )
  } else if (max > min && step > max - min) {
    errors.push(
      invalid(
        'track.numeric.step.tooLarge',
        `Step (${String(step)}) is larger than the range (${String(max - min)}).`,
        'kind.step',
      ),
    )
  }
  if (!Number.isInteger(decimals) || decimals < 0 || decimals > MAX_DECIMALS) {
    errors.push(
      invalid(
        'track.numeric.decimals.invalid',
        `Decimal places must be a whole number between 0 and ${String(MAX_DECIMALS)}.`,
        'kind.decimals',
      ),
    )
  }

  errors.push(...validateWords(kind))
  return errors
}

function validateWords(kind: NumericKind): ValidationError[] {
  const words = kind.words
  if (words === undefined || words.length === 0) {
    return []
  }

  const errors: ValidationError[] = []

  if (words.length > MAX_WORDS) {
    errors.push(
      invalid(
        'track.numeric.words.tooMany',
        `A scale can have at most ${String(MAX_WORDS)} words.`,
        'kind.words',
      ),
    )
  }

  const seen = new Set<number>()
  for (const word of words) {
    const label = word.label.trim()
    if (label === '') {
      errors.push(
        invalid('track.numeric.words.empty', 'A word needs some text.', 'kind.words'),
      )
    } else if (label.length > MAX_WORD_LENGTH) {
      errors.push(
        invalid(
          'track.numeric.words.tooLong',
          `A word can be at most ${String(MAX_WORD_LENGTH)} characters.`,
          'kind.words',
        ),
      )
    }

    if (!Number.isFinite(word.at) || word.at < kind.min || word.at > kind.max) {
      errors.push(
        invalid(
          'track.numeric.words.outOfRange',
          `"${label}" sits at ${String(word.at)}, outside the range ${String(kind.min)} to ${String(kind.max)}.`,
          'kind.words',
        ),
      )
    }

    if (seen.has(word.at)) {
      errors.push(
        invalid(
          'track.numeric.words.duplicate',
          `Two words share the point ${String(word.at)}.`,
          'kind.words',
        ),
      )
    }
    seen.add(word.at)
  }

  return errors
}

function validateBooleanKind(kind: BooleanKind): ValidationError[] {
  const errors: ValidationError[] = []
  if (kind.trueLabel.trim() === '' || kind.falseLabel.trim() === '') {
    errors.push(
      invalid(
        'track.boolean.labels.empty',
        'Both states need a label.',
        'kind.trueLabel',
      ),
    )
  } else if (kind.trueLabel.trim() === kind.falseLabel.trim()) {
    errors.push(
      invalid(
        'track.boolean.labels.duplicate',
        'The two states need different labels.',
        'kind.falseLabel',
      ),
    )
  }
  return errors
}

/**
 * The checks an option list needs whichever way it is selected from.
 *
 * Shared between `enum` and `multi` rather than copied, because these are the
 * rules that silently diverge: a duplicate-id check fixed in one place and not
 * the other is a bug that only appears on one of the two kinds. What is *not*
 * shared is the score rule, which is about how a list is read rather than about
 * the list itself.
 *
 * The error codes keep the `track.enum.` prefix on both. They are a contract —
 * screens and tests match on them — and renaming them to please a second caller
 * would be a breaking change in return for tidier strings.
 */
function validateOptionList(options: readonly EnumOption[]): ValidationError[] {
  const errors: ValidationError[] = []

  if (options.length < MIN_ENUM_OPTIONS) {
    errors.push(
      invalid(
        'track.enum.options.tooFew',
        `An option list needs at least ${String(MIN_ENUM_OPTIONS)} options.`,
        'kind.options',
      ),
    )
  } else if (
    options.filter((option) => option.archived !== true).length < MIN_ENUM_OPTIONS
  ) {
    // Counted separately from the total, because archiving enough options
    // leaves a track that still validates on paper and cannot be logged
    // against — a failure that appears at the moment of use, not of editing.
    errors.push(
      invalid(
        'track.enum.options.tooFewSelectable',
        `At least ${String(MIN_ENUM_OPTIONS)} options must stay available to choose.`,
        'kind.options',
      ),
    )
  }

  const ids = new Set<string>()
  const labels = new Set<string>()
  for (const option of options) {
    /*
     * An option colour goes straight into a `background`, where CSS values have
     * side effects — `url(https://…)` fetches when the swatch paints, from an
     * app whose promise is that nothing leaves the device. See `colour.ts`.
     */
    if (option.colour !== undefined && !isSafeColour(option.colour)) {
      errors.push(
        invalid(
          'track.enum.option.colour.invalid',
          `"${option.label}" needs a hex colour, like #8b939e.`,
          'kind.options',
        ),
      )
    }
    if (option.label.trim() === '') {
      errors.push(
        invalid(
          'track.enum.option.label.empty',
          'Every option needs a label.',
          'kind.options',
        ),
      )
    }
    if (ids.has(option.id)) {
      errors.push(
        invalid(
          'track.enum.option.id.duplicate',
          `Duplicate option id "${option.id}".`,
          'kind.options',
        ),
      )
    }
    ids.add(option.id)

    const label = option.label.trim().toLowerCase()
    if (labels.has(label)) {
      errors.push(
        invalid(
          'track.enum.option.label.duplicate',
          `Two options are both called "${option.label.trim()}".`,
          'kind.options',
        ),
      )
    }
    labels.add(label)
  }

  return errors
}

function validateEnumKind(kind: EnumKind): ValidationError[] {
  const errors = validateOptionList(kind.options)

  for (const option of kind.options) {
    // The ordered/unordered split is the whole reason an unordered enum cannot
    // produce a mean. Half-scored options would quietly break that guarantee.
    if (kind.ordered && option.score === null) {
      errors.push(
        invalid(
          'track.enum.option.score.missing',
          `"${option.label}" needs a position, because this scale is ordered.`,
          'kind.options',
        ),
      )
    }
    if (!kind.ordered && option.score !== null) {
      errors.push(
        invalid(
          'track.enum.option.score.unexpected',
          `"${option.label}" has a position, but this list is unordered.`,
          'kind.options',
        ),
      )
    }
    if (option.score !== null && !Number.isFinite(option.score)) {
      errors.push(
        invalid(
          'track.enum.option.score.invalid',
          'An option position must be a number.',
          'kind.options',
        ),
      )
    }
  }

  return errors
}

/**
 * A multi-select's options.
 *
 * The shared checks, plus the one rule of its own: no scores. There is no single
 * value to score when several are chosen at once, and a scored option here would
 * be a number nothing could read — the same reasoning that keeps an unordered
 * enum's options unscored.
 */
function validateMultiKind(kind: MultiKind): ValidationError[] {
  const errors = validateOptionList(kind.options)

  for (const option of kind.options) {
    if (option.score !== null) {
      errors.push(
        invalid(
          'track.enum.option.score.unexpected',
          `"${option.label}" has a position, but several of these can be chosen at once.`,
          'kind.options',
        ),
      )
    }
  }

  return errors
}

export function validateTrackKind(kind: TrackKind): Result<TrackKind> {
  const errors = matchTrackKind<ValidationError[]>(kind, {
    numeric: validateNumericKind,
    boolean: validateBooleanKind,
    enum: validateEnumKind,
    // Nothing to configure, so nothing to get wrong.
    marker: () => [],
    multi: validateMultiKind,
  })
  return errors.length > 0 ? err<TrackKind>(...errors) : ok(kind)
}

export interface ValidateTrackOptions {
  /** Used for the name-uniqueness check. Archived tracks are ignored. */
  readonly existingTracks?: readonly Track[]
}

export function validateTrack(
  track: Track,
  options: ValidateTrackOptions = {},
): Result<Track> {
  const errors: ValidationError[] = []
  const name = track.name.trim()

  if (name === '') {
    errors.push(invalid('track.name.empty', 'A track needs a name.', 'name'))
  } else if (name.length > MAX_NAME_LENGTH) {
    errors.push(
      invalid(
        'track.name.tooLong',
        `A name can be at most ${String(MAX_NAME_LENGTH)} characters.`,
        'name',
      ),
    )
  }

  const clash = (options.existingTracks ?? []).some(
    (other) =>
      other.id !== track.id &&
      !other.archived &&
      other.name.trim().toLowerCase() === name.toLowerCase(),
  )
  if (name !== '' && clash) {
    errors.push(
      invalid(
        'track.name.duplicate',
        `You already have a track called "${name}".`,
        'name',
      ),
    )
  }

  if (
    track.description !== undefined &&
    track.description.length > MAX_DESCRIPTION_LENGTH
  ) {
    errors.push(
      invalid(
        'track.description.tooLong',
        `A description can be at most ${String(MAX_DESCRIPTION_LENGTH)} characters.`,
        'description',
      ),
    )
  }

  if (track.question !== undefined && track.question.length > MAX_QUESTION_LENGTH) {
    errors.push(
      invalid(
        'track.question.tooLong',
        `A question can be at most ${String(MAX_QUESTION_LENGTH)} characters.`,
        'question',
      ),
    )
  }

  if (track.colour !== undefined && !isPaletteColour(track.colour)) {
    errors.push(
      invalid('track.colour.unknown', 'Choose one of the listed colours.', 'colour'),
    )
  }

  if (track.group !== undefined) {
    const group = track.group.trim()
    if (group === '') {
      errors.push(invalid('track.group.empty', 'A group needs a name.', 'group'))
    } else if (group.length > MAX_GROUP_LENGTH) {
      errors.push(
        invalid(
          'track.group.tooLong',
          `A group name can be at most ${String(MAX_GROUP_LENGTH)} characters.`,
          'group',
        ),
      )
    }
  }

  const kindResult = validateTrackKind(track.kind)
  if (!kindResult.ok) {
    errors.push(...kindResult.errors)
  }

  return errors.length > 0 ? err<Track>(...errors) : ok(track)
}

/**
 * Checks a value against the track it was recorded for.
 *
 * Errors name the specific bound or option that was broken, because the UI
 * shows them verbatim and "invalid value" tells the user nothing.
 */
export function validateTrackValue(track: Track, value: TrackValue): Result<TrackValue> {
  return matchTrackKind<Result<TrackValue>>(track.kind, {
    numeric: (kind) => {
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        return err<TrackValue>(
          invalid('value.type.mismatch', `${track.name} records a number.`, 'value'),
        )
      }
      if (value < kind.min) {
        return err<TrackValue>(
          invalid(
            'value.numeric.belowMinimum',
            `${track.name} cannot be below ${String(kind.min)}.`,
            'value',
          ),
        )
      }
      if (value > kind.max) {
        return err<TrackValue>(
          invalid(
            'value.numeric.aboveMaximum',
            `${track.name} cannot be above ${String(kind.max)}.`,
            'value',
          ),
        )
      }
      return ok(value)
    },
    boolean: () =>
      typeof value === 'boolean'
        ? ok(value)
        : err<TrackValue>(
            invalid('value.type.mismatch', `${track.name} records yes or no.`, 'value'),
          ),
    enum: (kind) => {
      if (typeof value !== 'string') {
        return err<TrackValue>(
          invalid(
            'value.type.mismatch',
            `${track.name} records one of its options.`,
            'value',
          ),
        )
      }
      return kind.options.some((option) => option.id === value)
        ? ok(value)
        : err<TrackValue>(
            invalid(
              'value.enum.unknownOption',
              `That is not an option on ${track.name}.`,
              'value',
            ),
          )
    },
    /*
     * `true` and nothing else — `false` is refused rather than stored.
     *
     * A stored `false` would be a recorded non-event, and the moment one exists
     * the count of markers stops being the count of occurrences: every consumer
     * would have to filter, and the ones that forgot would be quietly wrong.
     * "It did not happen" is the absence of a record (ADR-0005).
     */
    marker: () =>
      value === true
        ? ok(value)
        : err<TrackValue>(
            invalid(
              'value.marker.notRecorded',
              `${track.name} only records that it happened. Leave it out instead.`,
              'value',
            ),
          ),
    /*
     * An array of option ids, and never an empty one.
     *
     * Three refusals, each closing a different hole:
     *
     *   - **Empty is refused.** "Nothing selected" is the absence of a
     *     measurement, not a measurement of nothing — the same rule the marker
     *     follows for `false`, and the one that keeps a count of selections
     *     honest.
     *   - **Duplicates are refused.** A repeated id would count twice on every
     *     chart, and there is no user action that produces one.
     *   - **Unknown ids are refused**, as they are for an enum. History points
     *     at options by id, so an id nothing defines is unreadable.
     */
    multi: (kind) => {
      if (
        typeof value === 'number' ||
        typeof value === 'boolean' ||
        typeof value === 'string' ||
        value.some((id) => typeof id !== 'string')
      ) {
        return err<TrackValue>(
          invalid(
            'value.type.mismatch',
            `${track.name} records any number of its options.`,
            'value',
          ),
        )
      }
      const ids = value
      if (ids.length === 0) {
        return err<TrackValue>(
          invalid(
            'value.multi.empty',
            `Choosing nothing on ${track.name} records nothing. Leave it out instead.`,
            'value',
          ),
        )
      }
      if (new Set(ids).size !== ids.length) {
        return err<TrackValue>(
          invalid('value.multi.duplicate', 'An option was chosen twice.', 'value'),
        )
      }
      const unknown = ids.filter((id) => !kind.options.some((option) => option.id === id))
      return unknown.length === 0
        ? ok(ids)
        : err<TrackValue>(
            invalid(
              'value.enum.unknownOption',
              `That is not an option on ${track.name}.`,
              'value',
            ),
          )
    },
  })
}

/**
 * The numeric position of a value, for charting and statistics.
 *
 * `null` where no meaningful number exists — an unordered enum. Callers must
 * handle that rather than defaulting to zero: "no comparable value" and "zero"
 * are different claims, and conflating them corrupts every aggregate built on
 * top (T-401).
 */
export function quantify(track: Track, value: TrackValue): number | null {
  return matchTrackKind<number | null>(track.kind, {
    numeric: () => (typeof value === 'number' ? value : null),
    boolean: () => (typeof value === 'boolean' ? (value ? 1 : 0) : null),
    /*
     * `null`, not `1`, and the difference is the whole ticket.
     *
     * A marker quantified as 1 averages to exactly 1.0 for every marker track
     * that has ever been recorded — a number that is meaningful, constant and
     * completely useless, printed in a document people make decisions from.
     * What a marker supports is a *count*, which comes from the categorical
     * path.
     */
    marker: () => null,
    /*
     * `null`, and the tempting alternative is the number of options chosen.
     *
     * That number is real and sometimes exactly what somebody wants — "three
     * symptoms today, one yesterday". It is also nonsense on a track recording
     * ingredients, and this app does not know which it is looking at. Averaging
     * it would print a mean of "how many boxes were ticked" in a document
     * people make decisions from, and the honest place for that question is a
     * count on a chart rather than a magnitude on every reading.
     */
    multi: () => null,
    enum: (kind) => {
      if (!kind.ordered || typeof value !== 'string') {
        return null
      }
      return kind.options.find((option) => option.id === value)?.score ?? null
    },
  })
}

/**
 * What this track asks (T-810).
 *
 * Derived rather than stored when the track has no question of its own, and
 * that ordering is the point: `` `${name}?` `` computed on read follows a
 * rename, where a copy taken at creation would leave a track called "Sleep"
 * asking about "Kip" forever.
 *
 * In core rather than in the screen that shows it, because the native app will
 * ask the same questions and the report may print them. A second derivation is
 * a second answer waiting to disagree with the first.
 */
export function questionFor(track: Track): string {
  const own = track.question?.trim()
  return own === undefined || own === '' ? `${track.name}?` : own
}

/**
 * The word naming this point on a numeric scale, if the author gave it one.
 *
 * **The nearest anchor at or below**, not the nearest anchor. Words on a scale
 * describe a band rather than a point — "rough" anchored at 3 means three and
 * upwards until the next word starts, so 4 is still rough. Rounding to whichever
 * anchor is closest would make 4 read as whatever 5 is called, which is a word
 * for a value the user did not give.
 *
 * A value below the lowest anchor gets no word rather than the lowest one: the
 * author chose where to start naming, and inventing a name below that is a claim
 * they did not make.
 */
export function wordFor(kind: NumericKind, value: number): string | null {
  const words = kind.words
  if (words === undefined || words.length === 0) {
    return null
  }
  let best: ValueWord | null = null
  for (const word of words) {
    if (word.at <= value && (best === null || word.at > best.at)) {
      best = word
    }
  }
  return best === null ? null : best.label.trim()
}

/** The label to show for a value, resolved through the track's configuration. */
export function describeValue(track: Track, value: TrackValue): string | null {
  return matchTrackKind<string | null>(track.kind, {
    numeric: (kind) => (typeof value === 'number' ? value.toFixed(kind.decimals) : null),
    boolean: (kind) =>
      typeof value === 'boolean' ? (value ? kind.trueLabel : kind.falseLabel) : null,
    enum: (kind) =>
      typeof value === 'string'
        ? (kind.options.find((option) => option.id === value)?.label ?? null)
        : null,
    /*
     * One word, because the track's name has already said what happened.
     * Screens that have the name beside them render the chip without this;
     * a CSV cell under a column headed "Drank" needs something in it.
     */
    marker: () => (value === true ? 'Recorded' : null),
    /*
     * The chosen labels, in the **option list's** order rather than the order
     * they were tapped. The same set of answers has to read identically every
     * time, or two entries recording the same thing look different on the
     * timeline and in the export.
     */
    multi: (kind) => {
      if (
        typeof value === 'number' ||
        typeof value === 'boolean' ||
        typeof value === 'string'
      ) {
        return null
      }
      const ids = new Set(value)
      const chosen = kind.options
        .filter((option) => ids.has(option.id))
        .map((option) => option.label)
      return chosen.length === 0 ? null : chosen.join(' · ')
    },
  })
}
